/**
 * True/False Activity Slide
 * Binary choice question
 */

import { CheckCircle, XCircle, Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import type { ActivitySlideProps } from '../types';

export default function TrueFalseSlide({
  selectedAnswer,
  onAnswerChange,
  hasSubmitted,
  className = '',
}: ActivitySlideProps) {
  return (
    <div className={`max-w-xl mx-auto mb-8 ${className}`}>
      {/* Activity type badge */}
      <div className="flex items-center justify-center mb-6">
        <div className="inline-flex items-center gap-2 bg-indigo-500/10 text-indigo-600 px-4 py-1.5 rounded-full text-sm font-semibold">
          <Sparkles className="w-4 h-4" />
          True or False?
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
        {/* TRUE */}
        <motion.button
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          whileHover={!hasSubmitted ? { scale: 1.05, y: -4 } : {}}
          whileTap={!hasSubmitted ? { scale: 0.95 } : {}}
          onClick={() => !hasSubmitted && onAnswerChange(true)}
          disabled={hasSubmitted}
          className={`relative p-5 sm:p-10 rounded-3xl border-3 text-center transition-all duration-300 overflow-hidden ${
            selectedAnswer === true
              ? 'border-emerald-500 bg-emerald-500/10 shadow-xl shadow-emerald-500/20 ring-4 ring-emerald-500/20'
              : 'border-border/60 bg-card hover:border-emerald-400 hover:shadow-lg hover:bg-emerald-50'
          } ${hasSubmitted ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {selectedAnswer === true && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 3, opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="absolute inset-0 bg-emerald-500 rounded-3xl"
            />
          )}
          <div className="relative">
            <motion.div
              animate={selectedAnswer === true ? { rotate: [0, -5, 5, 0], scale: [1, 1.1, 1] } : {}}
              transition={{ duration: 0.5 }}
            >
              <CheckCircle className={`w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-3 transition-colors ${
                selectedAnswer === true ? 'text-emerald-500' : 'text-emerald-400/60'
              }`} strokeWidth={2} />
            </motion.div>
            <div className={`text-2xl sm:text-3xl font-extrabold transition-colors ${
              selectedAnswer === true ? 'text-emerald-600' : 'text-foreground'
            }`}>
              True
            </div>
          </div>
        </motion.button>

        {/* FALSE */}
        <motion.button
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          whileHover={!hasSubmitted ? { scale: 1.05, y: -4 } : {}}
          whileTap={!hasSubmitted ? { scale: 0.95 } : {}}
          onClick={() => !hasSubmitted && onAnswerChange(false)}
          disabled={hasSubmitted}
          className={`relative p-5 sm:p-10 rounded-3xl border-3 text-center transition-all duration-300 overflow-hidden ${
            selectedAnswer === false
              ? 'border-rose-500 bg-rose-500/10 shadow-xl shadow-rose-500/20 ring-4 ring-rose-500/20'
              : 'border-border/60 bg-card hover:border-rose-400 hover:shadow-lg hover:bg-rose-50'
          } ${hasSubmitted ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {selectedAnswer === false && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 3, opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="absolute inset-0 bg-rose-500 rounded-3xl"
            />
          )}
          <div className="relative">
            <motion.div
              animate={selectedAnswer === false ? { rotate: [0, 5, -5, 0], scale: [1, 1.1, 1] } : {}}
              transition={{ duration: 0.5 }}
            >
              <XCircle className={`w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-3 transition-colors ${
                selectedAnswer === false ? 'text-rose-500' : 'text-rose-400/60'
              }`} strokeWidth={2} />
            </motion.div>
            <div className={`text-2xl sm:text-3xl font-extrabold transition-colors ${
              selectedAnswer === false ? 'text-rose-600' : 'text-foreground'
            }`}>
              False
            </div>
          </div>
        </motion.button>
      </div>

      {/* Selection hint */}
      {!hasSubmitted && selectedAnswer === null && selectedAnswer !== false && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-sm text-muted-foreground mt-5 animate-pulse"
        >
          Tap True or False
        </motion.p>
      )}
    </div>
  );
}
